import { Component, computed, effect, inject, input } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";

import { ThemeService } from "@shared/services/theme/theme.service";
import { maskDocument } from "@modules/checkout/utils/document.utils";
import { CardComponent } from "@shared/components/card/card.component";
import { TableComponent } from "@shared/components/table/table.component";
import { IconFrameComponent } from "@shared/components/icon-frame/icon-frame.component";
import { IconComponent } from "@shared/components/icon/icon.component";
import { InputComponent } from "@shared/components/input/input.component";
import { SelectComponent } from "@shared/components/select/select.component";
import { ColumnsFor } from "@shared/types/table/column";
import { integerToCurrency } from "@shared/utils/currency.utils";
import { Product } from "@modules/checkout/types/product";
import { FancyRadioButtonComponent } from "@shared/components/fancy-radio-button/fancy-radio-button.component";
import { PAYMENT_METHODS_OPTIONS } from "@modules/checkout/constants/payment-methods-options";
import { PartySummaryComponent } from "@modules/checkout/components/party-summary/party-summary.component";
import { PaymentMethod } from "@modules/checkout/enums/payment-method";
import { SelectOption } from "@shared/types/select/select-option";
import { Mask } from "@modules/checkout/masks/mask";
import { RadioGroupComponent } from "@shared/components/radio-group/radio-group.component";
import { Transaction } from "@modules/checkout/types/transaction";
import { Installment } from "@modules/checkout/types/installment";

@Component({
  selector: "app-payment-form",
  imports: [
    PartySummaryComponent,
    CardComponent,
    TableComponent,
    IconFrameComponent,
    IconComponent,
    InputComponent,
    SelectComponent,
    ReactiveFormsModule,
    FancyRadioButtonComponent,
    RadioGroupComponent,
  ],
  templateUrl: "./payment-form.component.html",
  styleUrl: "./payment-form.component.scss",
})
export class PaymentFormComponent {
  constructor() {
    effect(() => {
      const methods = this.paymentMethods();
      if (methods.length === 1) {
        this.paymentMethodForm().setValue({
          method: methods[0].value as PaymentMethod,
        });
      }
    });
  }

  protected readonly theme = inject(ThemeService).theme;

  transaction = input<Nullable<Transaction>>(null);
  isLoading = input<boolean>(false);
  isCreditCard = input<boolean>(false);
  hasPaymentMethod = input<boolean>(false);
  paymentMethodForm = input.required<FormGroup<{ method: FormControl<Nullable<PaymentMethod>> }>>();
  creditCardForm = input.required<FormGroup>();
  selectedInstallmentNumber = input<number>();
  isSubmitting = input<boolean>(false);
  threeDSBlocking = input<boolean>(false);
  onSubmit = input.required<() => void>();

  protected readonly cardMask = Mask.card;
  protected readonly validThruMask = Mask.validThru;
  protected readonly cvvMask = Mask.cvv;

  protected readonly customer = computed(() => this.transaction()?.Customer);
  protected readonly customerName = computed(() => this.customer()?.Name ?? "");
  protected readonly customerDocument = computed(() => this.customer()?.Document ?? "");

  protected readonly receiverDocument = computed(() => {
    const transaction = this.transaction();
    return transaction?.Receiver?.Document ?? transaction?.Establishment?.CNPJ;
  });

  protected readonly receiverName = computed(() => {
    const transaction = this.transaction();
    return transaction?.Receiver?.Name ?? transaction?.Establishment?.FullName ?? "";
  });

  protected readonly receiverAvatarUrl = computed(() => {
    const transaction = this.transaction();
    return (
      (transaction?.Receiver
        ? transaction?.Receiver?.AvatarUrl
        : transaction?.Establishment?.AvatarUrl) ?? null
    );
  });

  protected readonly paymentMethods = computed(() =>
    PAYMENT_METHODS_OPTIONS.filter((method) =>
      this.transaction()?.PaymentRules.some((rule) => rule.Method === method.value),
    ),
  );

  protected readonly creditCardRules = computed(() =>
    this.transaction()?.PaymentRules.find((rule) => rule.Method === PaymentMethod.CreditCard),
  );

  protected readonly installmentOptions = computed<SelectOption<number>[]>(
    () =>
      this.creditCardRules()?.Installments.map((installment) => ({
        value: installment.Number,
        label: `${installment.Number}x de ${integerToCurrency(installment.InstallmentValue)}`,
        description: this.getInstallmentTotalLabel(installment),
      })) ?? [],
  );

  protected readonly products = computed(() => this.transaction()?.Products ?? []);
  protected readonly total = computed(() =>
    integerToCurrency(this.transaction()?.TotalAmount ?? 0),
  );

  protected readonly columns: ColumnsFor<Product> = [
    {
      key: "Name",
      label: "Descrição",
      variant: "highlight",
      className: "w-2/5",
    },
    {
      key: "Quantity",
      label: "Qtd.",
      className: "w-10 text-center",
    },
    {
      key: "Price",
      label: "Valor Unitário",
      className: "w-28",
      render: integerToCurrency,
    },
  ];

  protected getPartyName(name?: Nullable<string>) {
    return name ?? "";
  }

  protected getPartyDocument(document?: Nullable<string>) {
    return !document ? "" : maskDocument(document);
  }

  protected getSelectedInstallment(installments?: number | string) {
    return installments === undefined
      ? null
      : (this.creditCardRules()?.Installments.find((item) => item.Number === +installments) ??
          null);
  }

  protected getInstallmentTotalLabel(installment: Installment) {
    return `Total ${integerToCurrency(installment.FinalAmount)}`;
  }
}
